import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-apollo-hooks';
import { loader } from 'graphql.macro';
import debounce from 'debounce';

import { withGraphQLErrors } from '@loyalup/core';
import { Button, Field, Form, Loader } from '@loyalup/ui';

import { getClientFields } from 'components/ClientFields';
import updateProfileInCache from 'utils/updateProfileInCache';
import floorDecimal from 'utils/floorDecimal';

import { ReactComponent as ClientIcon } from 'icons/menu-clients.svg';
import { ReactComponent as BasketIcon } from 'icons/basket.svg';

import s from './ClientOrderForm.module.css';

const SAVE_CLIENT_MUTATION = loader('../../gql/mutations/SaveClient.gql');
const CREATE_CLIENT_ORDER_MUTATION = loader('../../gql/mutations/CreateClientOrder.gql');
const CALCULATE_CLIENT_ORDER_MUTATION = loader('../../gql/mutations/CalculateClientOrder.gql');

const useRawAmount = ({ client, order }) => {
  const defaultCache = { amount: '', limitBonusesSpend: 0, loyaltyPrograms: '' };

  const [cache, setCache] = useState(defaultCache);

  const { rawAmount, bonusesSpend, datetime } = order;
  const { id } = client || {};

  const [calcOrder, { loading }] = useMutation(CALCULATE_CLIENT_ORDER_MUTATION);

  const updateRawAmount = async () => {
    const resultCache = {};

    const getBonusesSpend = () => {
      if (!bonusesSpend) return 0;
      if (+bonusesSpend > +cache.limitBonusesSpend) {
        return cache.limitBonusesSpend;
      }
      return bonusesSpend;
    };

    await calcOrder({
      variables: {
        rawAmount,
        bonusesSpend: getBonusesSpend(),
        clientId: id,
        datetime,
      },
      update: (cache, { data: { calculateClientOrder } }) => {
        resultCache.amount = floorDecimal(calculateClientOrder.amount);
        resultCache.limitBonusesSpend = floorDecimal(calculateClientOrder.limitBonusesSpend);
        resultCache.loyaltyPrograms = calculateClientOrder.programs.map(({ translate }) => translate).join(', ');
      },
    });

    return resultCache;
  };

  useEffect(() => {
    if (rawAmount) {
      (async () => {
        setCache(await updateRawAmount());
      })();
    } else {
      setCache(defaultCache);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawAmount, bonusesSpend, datetime, id]);

  return {
    ...cache,
    loading,
  };
};

const onFormSubmit = async (
  client,
  saveClient,
  setClientErrors,
  setClient,
  order,
  createOrder,
  setOrderErrors,
  afterSave,
) => {
  setClientErrors({});
  setOrderErrors({});
  let savedClient;

  if (client) {
    const allFields = getClientFields({ asList: true });
    const fields = { id: client.id };
    allFields.forEach(({ name }) => {
      fields[name] = client[name] || null;
    });

    const { errors: clientErrors, data: clientData } = await withGraphQLErrors(() => saveClient({ variables: fields }));

    if (clientErrors) {
      setClientErrors(clientErrors);
      return;
    }

    savedClient = clientData.saveClient;
    setClient(savedClient);
  }

  const { errors: orderErrors } = await withGraphQLErrors(() =>
    createOrder({
      variables: {
        ...order,
        bonusesSpend: Math.max(order.bonusesSpend, 0),
        status: 'CONFIRMED',
        clientId: savedClient && savedClient.id,
      },
    }),
  );

  if (orderErrors) {
    setOrderErrors(orderErrors);
    return;
  }
  afterSave();
};

const ClientField = ({
  className,
  disabled,
  field,
  client,
  setClient,
  errors,
  loading,
  autoFocus,
  onChange,
  ...opts
}) => {
  const [t] = useTranslation();
  const { label, name, type, items, min, max, parse } = field;
  return (
    <Field
      autoFocus={autoFocus}
      className={cn(className, { [s.error]: errors[name] })}
      label={label(t)}
      type={type}
      disabled={disabled || loading}
      value={client[name] || ''}
      onChange={value => setClient({ ...client, [name]: parse(value) })}
      error={errors[name]}
      items={items}
      min={min}
      max={max}
      {...opts}
    />
  );
};

const ClientOrderForm = ({ order: originalOrder, afterSave, onCancel }) => {
  const [clientErrors, setClientErrors] = useState({});
  const [orderErrors, setOrderErrors] = useState({});
  const [order, setOrder] = useState({
    ...originalOrder,
    datetime: new Date().toISOString(),
    rawAmount: '',
    title: '',
    comment: '',
    bonusesSpend: '',
  });
  const [client, setClient] = useState();
  const [t] = useTranslation();
  const fields = getClientFields();

  const { amount, limitBonusesSpend, loyaltyPrograms, loading: calcLoading } = useRawAmount({
    client,
    order,
  });

  const [saveClient, { loading: clientLoading }] = useMutation(SAVE_CLIENT_MUTATION, {
    update: cache => {
      updateProfileInCache(cache, ({ clientsCount }) => ({
        //fix me
        clientsCount: client.id ? clientsCount : clientsCount + 1,
      }));
    },
  });
  const [createOrder, { loading: orderLoading }] = useMutation(CREATE_CLIENT_ORDER_MUTATION);

  const loading = clientLoading || orderLoading || calcLoading;

  return (
    <Form
      className={s.root}
      onSubmit={() =>
        onFormSubmit(client, saveClient, setClientErrors, setClient, order, createOrder, setOrderErrors, afterSave)
      }
      noContainers
    >
      <div className={s.container}>
        <h3 className={s.title}>
          <ClientIcon />
          {t('Информация о клиенте')}
        </h3>
        {client ? (
          <>
            <div className={s.threeColumns}>
              <ClientField
                autoFocus
                field={fields.firstName}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
                required
              />
              <ClientField
                field={fields.lastName}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
              />
              <ClientField
                field={fields.gender}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
              />
            </div>
            <div className={s.threeColumns}>
              <ClientField
                field={fields.phone}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
                required
              />
              <ClientField
                field={fields.email}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
              />
              <ClientField
                field={fields.bday}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
              />
            </div>
            <div className={s.threeColumns}>
              <ClientField
                field={fields.bonuses}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
                step="any"
              />
              <ClientField
                field={fields.discount}
                client={client}
                setClient={setClient}
                errors={clientErrors}
                loading={loading}
                step="any"
              />
            </div>
          </>
        ) : (
          <div className={s.columns}>
            <Field
              autoFocus
              label={t('Поиск клиента по имени, телефону или емейлу')}
              type="client"
              value={client}
              onChange={(_, item) => setClient(item.client)}
            />
            <div className={s.or}>{t('или')}</div>
            <Button
              className={s.addClient}
              fixedWidth={150}
              center
              type="button"
              onClick={() => setClient({ gender: 'UNKNOWN' })}
              children={t('Новый клиент')}
            />
          </div>
        )}
      </div>
      <div className={s.container}>
        <h3 className={s.title}>
          <BasketIcon />
          {t('Информация о заказе')}
        </h3>
        <div className={s.threeColumns}>
          <Field
            required
            label={t('Дата и время')}
            type="date"
            withTime
            value={order.datetime}
            onChange={datetime => setOrder({ ...order, datetime })}
            error={orderErrors.datetime}
          />
          <Field
            label={t('Наименование')}
            type="text"
            value={order.title}
            onChange={title => setOrder({ ...order, title })}
            error={orderErrors.title}
          />
        </div>
        <Field
          label={t('Комментарий')}
          type="text"
          value={order.comment}
          onChange={comment => setOrder({ ...order, comment })}
          error={orderErrors.comment}
        />
        <div className={s.threeColumns}>
          <Field
            required
            label={t('Сумма, ₽')}
            type="number"
            onChange={debounce(rawAmount => setOrder({ ...order, rawAmount }), 350)}
            error={orderErrors.rawAmount}
          />
          <Field
            label={
              limitBonusesSpend ? t('Списать бонусов из {{count}}', { count: limitBonusesSpend }) : t('Списать бонусов')
            }
            type="number"
            onChange={debounce(bonusesSpend => setOrder({ ...order, bonusesSpend }), 350)}
            error={orderErrors.bonusesSpend}
          />

          {calcLoading ? (
            <Loader className={s.fieldLoader} />
          ) : (
            <Field disabled label={t('Итоговая сумма, ₽')} type="number" value={amount} onChange={() => {}} />
          )}
        </div>
        <Field
          type="text"
          disabled
          label={t('Действующие программы лояльности')}
          value={loyaltyPrograms}
          onChange={() => {}}
        />
      </div>
      <div className={s.buttons}>
        <Button
          disabled={!amount || +order.bonusesSpend > +limitBonusesSpend}
          fixedWidth={150}
          center
          type="submit"
          loading={loading}
          children={t('Добавить заказ')}
        />
        <Button theme="black" type="button" disabled={loading} children={t('Отмена')} onClick={onCancel} />
      </div>
    </Form>
  );
};

ClientOrderForm.propTypes = {
  order: PropTypes.object.isRequired,
  afterSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ClientOrderForm;
